import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineTheatreIcon from 'LuxKit/Icons/line/LineTheatreIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineWellnessIcon from 'LuxKit/Icons/line/LineWellnessIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import Typography from 'LuxKit/Typography/Typography'
import { AnythingCategory, EXP_CATEGORY_LUX_PLUS_ID } from 'constants/experience'
import { getPlaceByRegionCode } from 'constants/places'
import GeoContext from 'contexts/geoContext'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { without } from 'lib/array/arrayUtils'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import useMostPopulatedSearchExperienceFacets from '../../hooks/useMostPopulatedSearchExperienceFacets'
import ExperienceAnythingCategoryIcon from './ExperienceAnythingCategoryIcon'
import Divider from 'LuxKit/Divider'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'

function useParentCategoryChildText(): Record<number, string> {
  const intl = useIntl()
  return useMemo(() => ({
    // Wine & dining
    1: intl.formatMessage({
      id: 'HZKg2UQcZB',
      defaultMessage: 'Fine dining, restaurant & bars, food tours, wine country trips and more.',
      description: 'Description for Wine & dining experience category in the experiences section',
    }),
    // Top activities
    9: intl.formatMessage({
      id: 'raFRqqqPg6',
      defaultMessage: 'Hot air ballooning, watersports, cruises, photoshoots and more.',
      description: 'Description text for the Top activities experience search category listing',
    }),
    // Indulge yourself
    31: intl.formatMessage({
      id: 'NUK3kYy1mY',
      defaultMessage: 'Spa & massage, hot springs, wellness and more.',
      description: 'Description for Indulge yourself experience category',
    }),
    // Attractions & tickets
    18: intl.formatMessage({
      id: 'b8GDc2A+Gz',
      defaultMessage: 'Theme & water parks, museums, galleries, zoos, historical sites and more.',
      description: 'Description for Attractions & tickets experience category',
    }),
    // Live events
    27: intl.formatMessage({
      id: 'hdk6R5B0hI',
      defaultMessage: 'Concerts, theatre, live sports and more.',
      description: 'Description for Live events experience category in the experiences section',
    }),
    // Airport lounges
    35: intl.formatMessage({
      id: 'd1fTv9AFzh',
      defaultMessage: 'Relax at the airport lounge, book a transfer and more.',
      description: 'Description for Airport lounges experience category',
    }),
    // Lux exclusive
    40: intl.formatMessage({
      id: '2H8uB7ns8n',
      defaultMessage: 'The best of the best.',
      description: 'Description for Lux exclusive experience category in the experiences section',
    }),
    // Day tours
    48: intl.formatMessage({
      id: 'nXlVC8EWxJ',
      defaultMessage: 'Guided tours, private tours, hop-on-hop-off buses, walking tours and more.',
      description: 'Description for Day tours experience category in the experiences section',
    }),
  }), [intl])
}

/**
 * Map of parent categoriy ids to the icon for them
 */
const parentCategoryToIconMap: Record<number, React.ReactElement> = {
  // Wine & dining
  1: <LineRestaurantIcon />,
  // Top activities
  9: <LineHotAirBalloonIcon />,
  // Indulge yourself
  31: <LineWellnessIcon />,
  // Attractions & tickets
  18: <LineTicketIcon />,
  // Live events
  27: <LineTheatreIcon />,
  // Airport lounges
  35: <LinePlaneDepartureIcon />,
  // Lux exclusive
  40: <LineStarIcon />,
  // Day tours
  48: <LineGuidedtoursIcon />,
  // Gift Inspiration
  56: <LineGiftIcon />,
  // LuxPlus
  [EXP_CATEGORY_LUX_PLUS_ID]: <LineAwardIcon />,
}

const GIFT_INSPIRATION_CATEGORY_ID = 56

interface Props {
  categories: Array<App.ExperienceItemCategory>
  className?: string
}

function ExperienceSearchCategoryResults(props: Props) {
  const { categories, className } = props
  const intl = useIntl()
  const parentCategoryChildText = useParentCategoryChildText()
  const { experienceCategories = [] } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const { currentRegionCode } = useContext(GeoContext)
  const regionPlace = getPlaceByRegionCode(currentRegionCode)

  const parentCategories = useMemo(() => categories.reduce<Record<number, Array<number>>>((acc, category) => {
    if (category.parentId) {
      if (!acc[category.parentId]) {
        acc[category.parentId] = []
      }
      acc[category.parentId].push(category.id)
    }
    return acc
  }, {}), [categories])

  const toggleCategory = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const categoryId = parseInt(e.currentTarget.dataset.catid!)
    let nextCategories: Array<number> = []
    if (parentCategories[categoryId]?.every(c => experienceCategories.includes(c))) {
      nextCategories = without(experienceCategories, ...parentCategories[categoryId])
    } else {
      nextCategories = [...parentCategories[categoryId], ...experienceCategories]
    }

    searchDispatch({
      type: GlobalSearchStateActions.SET_EXPERIENCE_CATEGORIES,
      categories: without(nextCategories, AnythingCategory.id),
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- searchDispatch is stable
  }, [experienceCategories, parentCategories])

  const { primaryCategories, fetching } = useMostPopulatedSearchExperienceFacets(regionPlace)

  const categoryItems = useMemo(() => {
    const items = primaryCategories.map(data => data.parent)
    const priorityCategory = items.find(item => item.id === GIFT_INSPIRATION_CATEGORY_ID)
    const otherCategories = items.filter(item => item.id !== GIFT_INSPIRATION_CATEGORY_ID)
    return priorityCategory ? [priorityCategory, ...otherCategories] : items
  }, [primaryCategories])

  const parentCategoriesStatus = useMemo<Record<string, 'full' | 'partial'>>(() => {
    const currentChildIds = new Set(experienceCategories)
    const categoriesStatuses: Record<string, 'full' | 'partial'> = {}
    for (const parentId in parentCategories) {
      const children = parentCategories[parentId]
      if (children.every(c => currentChildIds.has(c))) {
        categoriesStatuses[parentId] = 'full'
      } else if (children.some(c => currentChildIds.has(c))) {
        categoriesStatuses[parentId] = 'partial'
      }
    }
    return categoriesStatuses
  }, [experienceCategories, parentCategories])

  const onSelectAnything = useCallback(() => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_EXPERIENCE_CATEGORIES,
      categories: [AnythingCategory.id],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchDispatch is stable
  }, [])

  return <VerticalSpacer gap={8} className={className}>
    <ClickableListItem
      title={intl.formatMessage({
        id: '/Oew9UlRPR',
        defaultMessage: 'Anything',
        description: 'Category option label to show all experience categories',
      })}
      subtitle={intl.formatMessage({
        id: 'Ehxtc307Ah',
        defaultMessage: 'Inspire me with all experience categories',
        description: 'Subtitle for the "Anything" category option in experience search',
      })}
      startIcon={<ExperienceAnythingCategoryIcon />}
      onClick={onSelectAnything}
      type="submit"
    />
    <Divider kind="primary" />
    {fetching && <>
      <ListItemLoadingSkeleton subtitle startIcon />
      <ListItemLoadingSkeleton subtitle startIcon />
      <ListItemLoadingSkeleton subtitle startIcon />
    </>}
    {!fetching && categoryItems.map(cat => <ClickableListItem
      key={cat.id}
      title={cat.name}
      subtitle={<Typography wrap="truncate">{parentCategoryChildText[cat.id]}</Typography>}
      data-catid={cat.id}
      startIcon={parentCategoryToIconMap[cat.id]}
      endIcon={<CheckboxButton
        checked={parentCategoriesStatus[cat.id] === 'full'}
        indeterminate={parentCategoriesStatus[cat.id] === 'partial'}
        size="large"
      />}
      onClick={toggleCategory}
    />)}
  </VerticalSpacer>
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperienceSearchCategoryResults)
